/*
 * -----------------------------------------------------------------------
 *  <copyright file="render-field-input.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useRef } from 'react';
import CharactersRemaining from '@components/core/characters-remaining';
import { RenderFieldInputInterface } from '@components/core/editable-grid/editable-grid.interface';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import { useTranslation } from '@hooks/index';
import { validateFields } from '@utils/validation';

const RenderFieldInput = ({
    value,
    column,
    dataItem,
    onChange,
    errors,
    setErrors,
    currentOrderFocus,
    cursor,
    disabled = false,
    splitItemText = true,
    addNewAction,
}: RenderFieldInputInterface) => {
    const { t } = useTranslation();
    const validate = (val?: string, field?: string, length?: number) => {
        const error = validateFields(val, field, length);

        if (setErrors)
            setErrors({
                ...errors,
                ...error,
            });
    };

    const inputRef = useRef(null);
    useEffect(() => {
        const input: any = inputRef.current;
        if (input) input.setSelectionRange(cursor, cursor);
    }, [cursor]);

    switch (column.cellEditor) {
        case 'text':
            return (
                <TextField
                    inputRef={inputRef}
                    selectionRange
                    autoFocus={currentOrderFocus === dataItem.order}
                    id={`edit-field-${column.field}-${dataItem.order}`}
                    required={!!column.required}
                    fullWidth
                    value={value}
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (onChange) onChange(e);
                        validate(e.target.value, `${column.field}-${dataItem.order}`, 150);
                    }}
                    placeholder={t('admin:columnName')}
                    variant="outlined"
                    color="secondary"
                    error={!!errors[`${column.field}-${dataItem.order}`]}
                    helperText={
                        <CharactersRemaining
                            id={`error-input-${column.field}`}
                            length={value?.length || 0}
                            max={150}
                            error={errors[`${column.field}-${dataItem.order}`]}
                        />
                    }
                />
            );
        case 'select':
            return (
                <SelectWithCategories
                    onChange={() => onChange}
                    fieldSelect={column.field}
                    dataArray={
                        typeof column.selectValues === 'function'
                            ? column.selectValues(column.field, dataItem.order, value)
                            : column.selectValues
                    }
                    showWarning={false}
                    filterable={false}
                    disabled={disabled}
                    currentState={value}
                    customId={`edit-field-${column.field}`}
                    error={errors[`${column.field}-${dataItem.order}`]}
                    customClassName="w-full"
                    addNewAction={addNewAction}
                    searchable
                    splitItemText={splitItemText}
                />
            );
        default:
            return null;
    }
};

export default RenderFieldInput;
